/**
 * Every call the admin panel makes to /api/admin.
 *
 * WHY THIS IS ONE FUNCTION NOW
 *
 * It was three, copied across AdminShops, AdminShopDetail and AdminPaymentLink,
 * and all three called `r.json()` on whatever came back. Two of them did not
 * handle 401 either, so an expired session showed an empty panel rather than a
 * login screen.
 *
 * THE BUG THAT BROUGHT THIS TO LIGHT
 *
 * The panel stopped loading any data. The server was fine — /health 200, pm2
 * online, load average 0.09 — and the API was answering:
 *
 *   GET /api/admin/stats 200
 *   GET /api/admin/shops 304      <-- and from here on, nothing rendered
 *   GET /api/admin/stats 304
 *
 * A 304 Not Modified carries no body. `.json()` on an empty body throws, the
 * promise rejects, and the panel renders with no data and no error anyone
 * would connect to caching. First load works because it is a 200; every load
 * after it is a 304, which is why this looks like "it worked yesterday".
 *
 * The seller app already knew this. utils/shopifyFetch has handled it for a
 * while, in those words:
 *
 *   "A 204 or a 304 carries no body at all, and .json() on an empty body
 *    throws"
 *
 * The admin panel simply never got the same fix — the same shape as the
 * hubOnly rule that was applied to one nav and not its twin. Hence one
 * function, so the next fix lands everywhere at once.
 */

const TOKEN_KEY = 'admin_token'

/**
 * @param path   the part after /api/admin, e.g. '/shops'
 * @param token  the staff token
 * @param opts   fetch options; an object body is JSON-encoded
 */
export default function adminFetch(path, token, opts = {}) {
  return fetch(`/api/admin${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
    ...(opts.body
      ? { body: typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body) }
      : {}),
  }).then(r => {
    // An expired session is a login prompt, not an empty table. Two of the
    // three copies of this omitted it and showed a blank panel instead.
    if (r.status === 401) {
      try { localStorage.removeItem(TOKEN_KEY) } catch { /* blocked storage */ }
      window.location.reload()
      throw new Error('Session expired')
    }

    // No body to parse. Returning {} rather than throwing is what keeps a
    // cached response from emptying the screen — the caller already renders
    // from the state it is holding.
    if (r.status === 204 || r.status === 304) return {}

    // A body that is not JSON — an HTML error page from a proxy, say — would
    // otherwise surface as "Unexpected token <", which says nothing useful.
    return r.text().then(text => {
      if (!text) return {}
      try {
        return JSON.parse(text)
      } catch {
        throw new Error(`the server answered ${r.status} with something that is not JSON`)
      }
    })
  })
}

export { TOKEN_KEY }
